<template>
  <div>
    <!-- Top Navigation Bar (for non-dashboard pages) -->
    <div
      v-if="mode === 'topbar'"
      :class="{ 'solid-border': isSolidBorder }"
      class="header"
    >
      <div class="hero">
        <router-link to="/"><img src="@/assets/home-page.png" :alt="$t('navigation.homeAlt')" class="logo" /></router-link>
      </div>
      <div class="nav-bar">
        <!-- Mobile inline nav (replaces hamburger for native feel) -->
        <nav class="mobile-inline-nav">
          <a v-if="!isLoggedIn" @click="startAuth" class="mobile-login-link">
            {{ $t('navigation.login') }}
          </a>
          <router-link
            v-if="isLoggedIn"
            :to="Routes.DASHBOARD_ACCOUNT"
            class="mobile-login-link"
          >
            <font-awesome-icon icon="user" />
          </router-link>
        </nav>

        <nav class="desktop-nav">
          <ul>
            <li v-if="!isLoggedIn">
              <LanguageSwitcher />
            </li>
            <li>
              <router-link :to="Routes.BLOG" class="nav-link">{{ $t('navigation.blogs') }}</router-link>
            </li>
            <li>
              <router-link :to="Routes.FAQ" class="nav-link">{{ $t('navigation.faq') }}</router-link>
            </li>
            <li v-if="isLoggedIn">
              <router-link :to="Routes.DASHBOARD_ACCOUNT" class="nav-link">{{ $t('navigation.account') }}</router-link>
            </li>
            <li v-if="!isLoggedIn">
              <a @click="startAuth" class="nav-link">{{ $t('navigation.login') }}</a>
            </li>
            <li v-if="!isLoggedIn">
              <a @click="startAuth" class="nav-button">{{ $t('common.getStarted') }}</a>
            </li>
            <li v-if="isLoggedIn">
              <a @click="signOut" class="nav-link">{{ $t('navigation.logout') }}</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Side Navigation Panel (for dashboard) -->
    <div v-if="mode === 'sidebar'">
      <!-- Bottom Tab Bar for mobile viewports -->
      <BottomTabBar
        @add-language="$emit('add-language')"
        @upgrade-required="$emit('upgrade-required')"
      />

      <!-- Hamburger menu for mobile - hidden when bottom tab bar is visible -->
      <button class="hamburger-btn" @click="toggleNav" v-if="!isDesktop && !isMobileTabBar">
        <font-awesome-icon icon="bars" />
      </button>
      <!-- Overlay for mobile drawer -->
      <div
        v-if="showNav && !isDesktop"
        class="nav-overlay"
        @click="closeNav"
      ></div>
      <!-- Sidebar / Drawer (hidden on mobile when tab bar is active) -->
      <nav
        v-if="!isMobileTabBar"
        :class="[
          'navigation-panel',
          { open: showNav || isDesktop, collapsed: isCollapsed && isDesktop },
        ]"
      >
        <div class="nav-header">
          <div class="nav-title-container">
            <h1 v-if="!isCollapsed">ArticuLearn</h1>
          </div>
          <!-- Collapse/Expand button for desktop -->
          <button v-if="isDesktop" class="collapse-btn" @click="toggleCollapse">
            <font-awesome-icon
              :icon="isCollapsed ? 'chevron-right' : 'chevron-left'"
            />
          </button>
        </div>
        <div class="nav-main">
          <!-- First, like the bottom tab bar's Today tab, and gated on the same
               runtime flag. featureFlags is reactive, so the entry appears as
               soon as the flag's listeners answer rather than at the next
               remount of the sidebar. -->
          <NavigationItem
            v-if="featureFlags.dailyPlay"
            :to="Routes.DASHBOARD_TODAY"
            icon="calendar-check"
            :label="isCollapsed ? '' : $t('navigation.today')"
          />
          <NavigationItem
            :to="Routes.DASHBOARD_COURSE"
            icon="book"
            :label="isCollapsed ? '' : $t('navigation.myCourse')"
          />
          <NavigationItem
            :to="Routes.DASHBOARD_CHAT"
            icon="comments"
            :label="isCollapsed ? '' : $t('navigation.myChats')"
          />
          <NavigationItem
            :to="Routes.DASHBOARD_ACCOUNT"
            icon="user"
            :label="isCollapsed ? '' : $t('navigation.account')"
          />
        </div>
        <div class="nav-footer">
          <LearningLanguageSwitcher
            v-if="!isCollapsed"
            @add-language="$emit('add-language')"
            @upgrade-required="$emit('upgrade-required')"
          />
          <button class="sign-out-btn" @click="signOut">
            <font-awesome-icon icon="fa-solid fa-sign-out-alt" />
            <span v-if="!isCollapsed" class="sign-out-text">{{ $t('navigation.signOut') }}</span>
          </button>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import NavigationItem from "./NavigationItem.vue";
import BottomTabBar from "./BottomTabBar.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import LearningLanguageSwitcher from "./LearningLanguageSwitcher.vue";
import { authStore } from "@/store/auth.js";
import { authUtils } from "@/scripts/authUtils.js";
import { ref as vueRef, computed, onMounted, onUnmounted } from "vue";
import { Routes } from "@/constants/routes.js";
import { featureFlags } from "@/store/featureFlags.js";

export default {
  name: "AppNavigation",
  components: {
    NavigationItem,
    BottomTabBar,
    LanguageSwitcher,
    LearningLanguageSwitcher,
  },
  emits: ["add-language", "upgrade-required"],
  props: {
    // 'topbar' for NavigationBar or 'sidebar' for NavigationPanel
    mode: {
      type: String,
      required: true,
      validator: (value) => ["topbar", "sidebar"].includes(value),
    },
  },
  setup(props) {
    // Shared data
    const isLoggedIn = computed(() => !!authStore.getUserId());

    // For NavigationBar
    const isSolidBorder = vueRef(false);
    const scrollThreshold = 100;

    // For NavigationPanel
    const showNav = vueRef(false);
    const isDesktop = vueRef(window.innerWidth >= 1024);
    const isMobileTabBar = vueRef(window.innerWidth <= 768);
    const isCollapsed = vueRef(false);

    // Shared methods
    const handleSignOut = async () => {
      await authUtils.signOut();
    };

    const handleScroll = () => {
      isSolidBorder.value = window.scrollY > scrollThreshold;
    };

    // NavigationPanel methods
    const toggleNav = () => {
      showNav.value = !showNav.value;
    };

    const closeNav = () => {
      showNav.value = false;
    };

    const handleResize = () => {
      isDesktop.value = window.innerWidth >= 1024;
      isMobileTabBar.value = window.innerWidth <= 768;
      if (isDesktop.value) {
        showNav.value = true;
      } else {
        showNav.value = false;
      }
    };

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
      // Emit sidebar-collapsed event for DashboardView
      if (props.mode === "sidebar") {
        window.dispatchEvent(
          new CustomEvent("sidebar-collapsed", { detail: isCollapsed.value }),
        );
      }
    };

    onMounted(() => {
      if (props.mode === "topbar") {
        window.addEventListener("scroll", handleScroll);
        handleScroll();
      } else if (props.mode === "sidebar") {
        window.addEventListener("resize", handleResize);
        handleResize();
      }
    });

    onUnmounted(() => {
      if (props.mode === "topbar") {
        window.removeEventListener("scroll", handleScroll);
      } else if (props.mode === "sidebar") {
        window.removeEventListener("resize", handleResize);
      }
    });

    return {
      // Constants
      Routes,
      featureFlags,

      // Shared data
      isLoggedIn,

      // TopBar data
      isSolidBorder,

      // Sidebar data
      showNav,
      isDesktop,
      isMobileTabBar,
      isCollapsed,

      // Methods
      startAuth: authUtils.startAuth,
      signOut: handleSignOut,
      toggleNav,
      closeNav,
      toggleCollapse,
    };
  },
};
</script>

<style scoped>
/* ====== SHARED STYLES ====== */
/* Scoped :root is fragile in Vue SFCs; attach local aliases to the real root
   wrappers so they cascade to every consumer inside them. */
.header, .navigation-panel, .bottom-tab-bar, .hamburger-btn {
  --primary-color: var(--al-primary);
  --secondary-color: var(--al-primary);
  --light-gray: var(--al-surface-sunken);
  --medium-gray: var(--al-border);
  --text-color: var(--al-text);
}

/* ====== NAVIGATION BAR STYLES (topbar mode) ====== */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: var(--al-topbar-height);
  padding: 0 2rem;
  background-color: var(--al-surface-raised);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.solid-border {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid var(--primary-color);
}

.hero {
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.logo {
  height: 50px;
  width: auto;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

/* Mobile inline nav (replaces hamburger for native feel) */
.mobile-inline-nav {
  display: none;
  align-items: center;
  gap: 0.75rem;
}

.mobile-login-link {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  cursor: pointer;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.mobile-login-link:active {
  background-color: color-mix(in srgb, var(--al-primary) 10%, transparent);
}

/* For topbar mobile screens */
@media (max-width: 767px) {
  .header {
    padding: 0 1rem;
    /* --al-topbar-height already includes the safe-area inset on mobile; the
       inset must grow the bar, not eat into the 56px content box — otherwise
       the logo/icons overflow and float over page content. */
    height: var(--al-topbar-height);
    padding-top: var(--safe-top, env(safe-area-inset-top, 0px));
  }

  .mobile-inline-nav {
    display: flex;
  }

  .desktop-nav {
    display: none;
  }

  .hero {
    flex: 1;
    display: flex;
    justify-content: flex-start;
  }

  .logo {
    height: 36px;
  }
}

/* Small phones */
@media (max-width: 374px) {
  .logo {
    height: 32px;
  }
}

/* For topbar desktop screens */
@media (min-width: 768px) {
  .desktop-nav {
    margin-top: 20px;
    display: block;
  }

  .desktop-nav ul {
    list-style: none;
    display: flex;
    justify-content: center;
  }

  .desktop-nav .nav-link {
    padding: 0.6rem 1rem;
    text-decoration: none;
    color: var(--text-color);
    font-weight: 600;
    font-size: 1rem;
    font-family: inherit;
    display: inline-block;
    margin: 0 0.25rem;
    transition: all 0.3s ease;
    border-radius: 0.5rem;
    position: relative;
  }

  .desktop-nav .nav-link::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background-color: var(--primary-color);
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  .desktop-nav .nav-link:hover {
    color: var(--primary-color);
    background-color: color-mix(in srgb, var(--al-primary) 12%, transparent);
    cursor: pointer;
  }

  .desktop-nav .nav-link:hover::after {
    width: 70%;
  }

  .desktop-nav .nav-button {
    padding: 0.7rem 1.5rem;
    border-radius: 0.5rem;
    background-color: var(--al-primary);
    color: #fff;
    border: none;
    cursor: pointer;
    text-decoration: none;
    font-weight: 700;
    font-size: 1rem;
    font-family: inherit;
    display: inline-block;
    margin: 0 0.5rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 8px rgba(45, 144, 235, 0.3);
    transform: translateY(0);
    letter-spacing: 0.5px;
  }

  .desktop-nav .nav-button:active,
  .desktop-nav .nav-button:hover {
    background-color: var(--al-primary-hover);
    color: #fff;
  }

  .mobile-inline-nav {
    display: none;
  }
}

/* For extra-large screens */
@media (min-width: 1900px) {
  .logo {
    width: 12vw;
  }
}

/* ====== NAVIGATION PANEL STYLES (sidebar mode) ====== */
.hamburger-btn {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 2100;
  /* Increased to ensure it's above everything else */
  background: var(--al-surface-raised);
  border: none;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  cursor: pointer;
}

@media (max-width: 767px) {
  .hamburger-btn {
    top: 1rem;
    left: 0.75rem;
    padding: 0.5rem;
    font-size: 1.1rem;
    border-radius: 6px;
    width: 36px;
    height: 36px;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    background-color: color-mix(in srgb, var(--al-surface-raised) 95%, transparent);
    border: 1px solid color-mix(in srgb, var(--al-primary) 15%, transparent);
    color: var(--primary-color);
  }
}

.collapse-btn {
  position: absolute;
  top: 1.25rem;
  right: -0.5rem;
  background: var(--al-surface-raised);
  border: none;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2003;
}

.nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--al-scrim);
  z-index: 2000;
}

.navigation-panel {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  height: calc(100% - env(safe-area-inset-bottom, 0px));
  width: 220px;
  background-color: var(--al-surface-sunken);
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 2002;
  transform: translateX(-100%);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 20px);
}

.navigation-panel.open {
  transform: translateX(0);
}

.navigation-panel.collapsed {
  width: 64px;
}

.nav-header {
  padding: 1.5rem 1rem 1rem 1rem;
  border-bottom: 1px solid var(--al-border);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--al-surface-raised);
}

.nav-title-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.nav-header h1 {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--primary-color);
  margin: 0;
  text-align: center;
  transition: opacity 0.2s;
}

.nav-main {
  flex-grow: 1;
  padding: 2rem 0 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  width: 100%;
  overflow-x: hidden;
}

.navigation-panel.collapsed .nav-main {
  align-items: center;
  padding-left: 0;
  padding-right: 0;
  width: 100%;
}

.nav-footer {
  border-top: 1px solid var(--al-border);
  padding: 1.5rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  justify-content: flex-start;
  background-color: var(--al-surface-raised);
  margin-top: auto;
}

.navigation-panel.collapsed .nav-footer {
  flex-direction: column;
  align-items: center;
  padding: 1rem 0;
  justify-content: center;
}

.current-language {
  padding: 0.5rem;
  border-radius: 8px;
  background-color: var(--light-gray);
}

.language-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 4px;
}

.sign-out-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--al-border);
  border-radius: 8px;
  background-color: var(--al-surface-raised);
  color: var(--al-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  box-shadow: var(--al-shadow-sm);
  min-width: 44px;
  min-height: 44px;
  justify-content: center;
}

.sign-out-btn:hover {
  background-color: var(--al-surface-sunken);
  /* AA: primary blue on the sunken fill is ~3.2:1; the bg change + shadow
     carry the hover affordance, so text stays at full contrast. */
  color: var(--al-text);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--al-primary) 15%, transparent);
  transform: translateY(-1px);
}

.sign-out-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px color-mix(in srgb, var(--al-primary) 10%, transparent);
}

.sign-out-text {
  display: inline-block;
  transition: transform 0.2s ease;
}

.sign-out-btn:hover .sign-out-text {
  transform: translateX(2px);
}

@media (min-width: 1024px) {
  .hamburger-btn {
    display: none;
  }

  .nav-overlay {
    display: none;
  }

  .navigation-panel {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: 250px;
    transform: translateX(0);
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    overflow-x: hidden;
  }

  .navigation-panel.open {
    transform: translateX(0);
  }

  .navigation-panel.collapsed {
    width: 64px;
  }
}

@media (max-width: 1023px) {
  .navigation-panel {
    width: 80vw;
    max-width: 320px;
    background-color: var(--al-surface-raised);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
  }

  .nav-header {
    background-color: var(--al-surface-raised);
    border-bottom: 1px solid var(--al-border);
    justify-content: center;
    padding: 1.5rem 1rem 1rem;
    height: 70px;
  }

  .nav-title-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .nav-header h1 {
    color: var(--primary-color);
    font-weight: bold;
    font-size: 1.3rem;
    text-align: center;
  }

  .nav-main {
    background-color: var(--al-surface-raised);
    margin-top: 0.5rem;
    flex: 1 1 auto;
    overflow-y: auto;
  }

  .nav-main a {
    color: var(--al-text);
    font-weight: 500;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .current-language {
    background-color: var(--al-surface-sunken);
  }

  .nav-footer {
    background-color: var(--al-surface-raised);
    border-top: 1px solid var(--al-border);
    padding: 1.5rem;
    justify-content: center;
    flex-shrink: 0;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 10px));
  }

  .sign-out-btn {
    background-color: var(--al-surface-raised);
    color: var(--al-text-muted);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    width: 80%;
    justify-content: center;
    margin: 0 auto;
    font-size: 1rem;
    letter-spacing: 0.5px;
  }

  .sign-out-text {
    color: var(--al-text-muted);
  }
}

/* Specific styles for Pixel 9 and similar Android devices */
@media (max-width: 767px) and (min-resolution: 2.5dppx) {
  .sign-out-btn {
    margin-bottom: calc(1rem + env(safe-area-inset-bottom, 10px));
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    background-color: var(--al-surface-raised);
    color: var(--al-text-muted);
    width: 90%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .nav-footer {
    padding-bottom: calc(2rem + env(safe-area-inset-bottom, 30px));
    flex-direction: column;
  }

  .current-language {
    margin-bottom: 1rem;
    padding: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .sign-out-text {
    color: var(--al-text-muted);
    font-weight: 600;
  }

  .navigation-panel {
    height: 100%;
    height: -webkit-fill-available;
    max-height: -webkit-fill-available;
  }
}
</style>
